/**
 * app.js — страница графика домашних дел: что открыть при загрузке и кто чем
 * управляет.
 *
 * Четыре входа, один и тот же интерфейс поверх разных хранилищ:
 *  • гость — локальный график в браузере (первый экран нарисовал сервер,
 *    нажатие раньше скрипта донёс window.__earlyAction);
 *  • вошедший — общий дом с сервера (снимок приехал вместе со страницей);
 *  • ссылка-приглашение (#join=…) — показать, куда зовут, и войти в дом;
 *  • ссылка настенного экрана (#display=…) — доска «сегодня» во всё окно.
 *
 * «Поделиться» — момент, когда нужен аккаунт: локальный график после входа
 * переезжает на сервер целиком (люди, дела, журнал, очередь), и только тогда
 * появляются приглашения.
 */

import * as E from './engine.js';
import { LocalStore, ServerStore, DisplayStore, Refusal, account } from './store.js';
import { starter } from './templates.js';
import {
    T, CFG, t, plural, num, h, clear, avatar, memberName, dialog, closeDialog, btn, seg, toast, fail, announce,
    confirmDialog, dayShort, premiumLine,
} from './ui.js';
import { renderToday, renderWeek, renderMonth, renderStars, renderPeople } from './views.js';
import { renderBalance } from './charts.js';
import { choreDialog, setsDialog, peopleDialog, homeDialog, rewardDialog } from './forms.js';
import { mountDisplay } from './display.js';

const SLUG = 'chore-chart';
const LS_JOIN = 'cc_join_v1';
const LS_MOVE = 'cc_move_v1';

const $ = (id) => document.getElementById(id);
const root = $('hcRoot');
const startEl = $('hcStart');
const appEl = $('hcApp');
const menu = $('hcMenu');

const lsGet = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const lsSet = (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (e) { /* приватное окно */ } };
const stage = (n) => { try { if (window.__stage) window.__stage(n); } catch (e) { /* телеметрия не мешает работе */ } };
const funnel = (n, d) => { try { if (window.__aeFunnel) window.__aeFunnel(n, d || {}); } catch (e) { /* то же */ } };

function hashParam(name) {
    const m = new RegExp('(?:^|[#&])' + name + '=([a-f0-9]{48})(?:&|$)').exec(location.hash || '');
    return m ? m[1] : null;
}
function clearHash() { try { history.replaceState(null, '', location.pathname + location.search); } catch (e) { /* старый движок */ } }
function deviceTz() { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (e) { return 'UTC'; } }
/* Неделя с воскресенья — там, где так принято (Америки, Корея, Япония); это только умолчание, дом его меняет. */
function weekStartGuess() { return /^(America\/|Asia\/(Seoul|Tokyo)|Pacific\/Honolulu)/.test(deviceTz()) ? 7 : 1; }

/* ── приложение ────────────────────────────────────────────────────────── */

const app = {
    T,
    store: null,
    state: null,
    today: null,
    view: 'today',
    filter: 'all',
    weekOf: null,
    monthOf: null,
    dayOpen: null,
    balPeriod: 'week',
    balMode: 'time',
    balTable: false,
    wide: false,
    chartW: 560,
    justKey: null,
    celebrate: false,
    oldest: null,
    hist: null,
    me: CFG.boot && CFG.boot.me,
    can: {},
    act: {},

    member(id) { return (this.state.members || []).find((m) => m.id === id) || null; },

    go(patch) {
        Object.assign(this, patch);
        if (patch.dayOpen !== undefined || patch.view) this._scrollDay = !!patch.dayOpen;
        render();
        if (this._scrollDay && this.view === 'month') { const box = $('hcDayBox'); if (box) box.scrollIntoView({ block: 'nearest' }); }
    },

    async op(name, a) {
        const res = await this.store.op(name, a || {});
        return res;
    },

    tickable(date) { return Math.abs(E.diffDays(date, this.today)) <= 7; },

    pendingHand(it) {
        if (it.planned == null) return null;
        return (this.state.handovers || []).find((x) => x.status === 'pending' && x.chore_id === it.chore.id && x.date === it.date && x.from_id === (it.from != null ? it.from : it.planned)) || null;
    },

    /** Журнал за период: из снимка, если период в его окне, иначе с сервера (и пока его нет — null). */
    entriesFor(from) {
        const s = this.state;
        const floor = E.addDays(this.today, -41);
        if (this.store.mode === 'local' || from >= floor) return s.log;
        const key = from + '|' + s.rev;
        if (this.hist && this.hist.key === key) return this.hist.log;
        if (!this._histBusy) {
            this._histBusy = true;
            this.store.history(from, this.today).then((r) => {
                this.hist = { key, from, log: r.log }; this.oldest = r.oldest || this.oldest;
            }).catch(() => { this.hist = { key, from, log: s.log }; }).then(() => { this._histBusy = false; render(); });
        }
        return this.hist && this.hist.from <= from ? this.hist.log : null;
    },

    stateFor(from) {
        const log = this.entriesFor(from);
        if (!log || log === this.state.log) return this.state;
        if (this._sf && this._sf.log === log && this._sf.base === this.state) return this._sf.state;
        const state = Object.assign({}, this.state, { log });
        this._sf = { log, base: this.state, state };
        return state;
    },

    /* Входящие: просьбы взять чужой раз — мне или тем, за кого отвечаю я. */
    inbox() {
        const s = this.state, me = s.me && s.me.member_id;
        const out = [];
        (s.handovers || []).filter((x) => x.status === 'pending').forEach((x) => {
            const to = this.member(x.to_id), from = this.member(x.from_id), ch = (s.chores || []).find((c) => c.id === x.chore_id);
            if (!to || !ch) return;
            const mine = x.to_id === me;
            const forProfile = this.can.manage && !mine;
            if (!mine && !forProfile) return;
            out.push(h('div.hc-ask', null, [
                avatar(from, 26),
                h('span.hc-ask-t', null, mine
                    ? t('hand_inbox', { from: memberName(from), chore: ch.title, day: dayShort(x.date) })
                    : t('hand_inbox_for', { from: memberName(from), to: memberName(to), chore: ch.title, day: dayShort(x.date) })),
                btn(t('hand_accept'), () => run('handover.answer', { id: x.id, accept: true }), 'sm.primary'),
                btn(t('hand_decline'), () => run('handover.answer', { id: x.id, accept: false }), 'sm.ghost'),
            ]));
        });
        return out;
    },

    menuItems(it) {
        const s = this.state, me = s.me && s.me.member_id;
        const items = [];
        if (it.status === 'open') {
            if (this.can.manage && s.members.filter((m) => !m.left).length > 1) items.push({ label: t('m_done_as'), sub: 'as' });
            if (this.can.skip(it)) items.push({ label: t('m_skip'), sub: 'skip' });
            const ask = this.pendingHand(it);
            if (ask && (ask.from_id === me || this.can.manage)) items.push({ label: t('hand_cancel'), run: () => run('handover.cancel', { id: ask.id }) });
            else if (s.mode !== 'local' && it.planned != null && it.chore.kind !== 'after' && (it.planned === me || this.can.manage)) items.push({ label: t('m_hand'), sub: 'hand' });
        } else if (this.can.undo(it)) {
            items.push({ label: t('undo'), run: () => this.act.undo(it) });
        }
        if (this.can.manage && !it.chore.gone) items.push({ label: t('m_edit'), run: () => choreDialog(this, it.chore) });
        return items;
    },

    fillMenu(it, door, sub) {
        const s = this.state;
        clear(menu);
        const item = (label, fn, keep, node) => menu.appendChild(h('button.hc-menu-i', { type: 'button', 'data-pop-keep': keep ? '' : null, onclick: fn }, [node || null, h('span', null, label)]));
        if (!sub) {
            this.menuItems(it).forEach((mi) => item(mi.label, mi.sub ? () => this.fillMenu(it, door, mi.sub) : mi.run, !!mi.sub));
            return;
        }
        menu.appendChild(h('div.hc-menu-h', null, sub === 'skip' ? t('reason_title') : (sub === 'hand' ? t('hand_title', { chore: it.chore.title }) : t('m_done_as'))));
        if (sub === 'skip') {
            ['sick', 'away', 'not_needed', 'no_time', 'other'].forEach((r) => item(t('reason_' + r), () => this.act.skip(it, r)));
        } else {
            const people = s.members.filter((m) => !m.left && (sub === 'as' || (m.id !== it.planned && !E.isAway(m, it.date))));
            people.forEach((m) => item(memberName(m), () => (sub === 'as' ? this.act.tick(it, m.id) : this.act.hand(it, m.id)), false, avatar(m, 24)));
        }
        if (window.__anchoredPopover && door) { try { window.__anchoredPopover.anchorTo(menu, door); } catch (e) { /* ярус без якоря */ } }
    },

    /** Выбор значка: в диалоге — раскрывающаяся сетка под кнопкой (модальный диалог глушит всё вне себя), на странице — меню у кнопки. */
    pick(door, list, cb) {
        const dlg = door.closest('dialog');
        if (dlg) {
            const old = dlg.querySelector('.hc-pickgrid');
            if (old) { old.remove(); return; }
            const grid = h('div.hc-pickgrid', null, list.map((e) => h('button.hc-pick-e', { type: 'button', onclick: () => { grid.remove(); cb(e); } }, e)));
            (door.closest('.hc-form-row') || door).after(grid);
            return;
        }
        clear(menu);
        menu.appendChild(h('div.hc-pickgrid', null, list.map((e) => h('button.hc-pick-e', { type: 'button', onclick: () => cb(e) }, e))));
    },
};

async function run(op, a) {
    try { return await app.op(op, a); } catch (e) { fail(e, 'cc_' + op.replace('.', '_')); return null; }
}

/* Что роли положено — чтобы не предлагать того, в чём сервер откажет. Решает всё равно сервер. */
function permissions(s) {
    const me = s.me && s.me.member_id;
    const role = (s.me && s.me.role) || 'member';
    const manage = role === 'owner' || role === 'admin';
    return {
        manage,
        tick: (it) => it.status === 'open' && (role !== 'child' || it.planned == null || it.planned === me),
        skip: (it) => it.status === 'open' && (manage || (role !== 'child' && it.planned === me)),
        undo: (it) => !!it.entry && !it.entry.pending && (manage || ((it.entry.member_id === me || it.entry.by_id === me) && it.entry.on === s.today)),
        away: (m) => (manage && (m.id === me || role === 'owner' || m.role === 'member' || m.role === 'child')) || (m.id === me && role !== 'child'),
        manageMember: (m) => manage && (m.id === me || role === 'owner' || m.role === 'member' || m.role === 'child'),
    };
}

/* ── действия ──────────────────────────────────────────────────────────── */

let firstTick = true;

Object.assign(app.act, {
    async tick(it, asMember) {
        const s = app.state;
        const me = s.me && s.me.member_id;
        /* Кому зачесть. Свой раз — себе. Раз человека БЕЗ аккаунта (ребёнок, бабушка; на
           локальном графике — все) организатор отмечает за него: сам тот отметить не может.
           Раз человека с аккаунтом, сделанный мной, — мой («сделал за тебя»). */
        const planned = it.planned != null ? app.member(it.planned) : null;
        const forThem = planned && planned.id !== me && app.can.manage && (planned.kind === 'profile' || it.chore.mode === 'each');
        const who = asMember || (forThem ? planned.id : me);
        const wasLast = (() => {
            const b = E.board(s, app.today);
            const open = b.today.concat(b.carry).filter((x) => x.status === 'open' && (app.filter === 'all' || x.planned === who || x.planned == null));
            return open.length === 1 && open[0].key === it.key;
        })();
        app.justKey = it.chore.kind === 'after' ? E.entryKey(it.chore.id, app.today, 0) : it.key;
        try {
            const res = await app.op('tick.done', { chore_id: it.chore.id, date: it.date, member_id: who === me ? undefined : who });
            if (res.already && res.entry && res.entry.member_id !== who) toast(t('err_already', { name: memberName(app.member(res.entry.member_id)) }), 'info');
            else if (res.queued) toast(t('offline'), 'warning');
            else announce(t('done') + ': ' + it.chore.title);
            if (wasLast) { app.celebrate = true; setTimeout(() => { app.celebrate = false; }, 1600); render(); }
            if (firstTick) {
                firstTick = false;
                funnel(s.mode === 'local' ? 'first_tick_local' : 'first_tick_shared', { people: s.members.length });
                try { if (window.__aeToolComplete) window.__aeToolComplete({ mode: s.mode, chores: s.chores.length, people: s.members.length }); } catch (e) { /* телеметрия */ }
            }
        } catch (e) { fail(e, 'cc_tick'); }
        setTimeout(() => { if (app.justKey) { app.justKey = null; } }, 900);
    },

    async undo(it) {
        if (!it.entry) return;
        try { await app.op('tick.undo', { entry_id: it.entry.id }); } catch (e) { fail(e, 'cc_undo'); }
    },

    async skip(it, reason) {
        try { await app.op('tick.skip', { chore_id: it.chore.id, date: it.date, member_id: it.planned != null && it.planned !== (app.state.me && app.state.me.member_id) ? it.planned : undefined, reason }); } catch (e) { fail(e, 'cc_skip'); }
    },

    async hand(it, to) {
        const res = await run('handover.ask', { chore_id: it.chore.id, date: it.date, to_id: to, from_id: it.planned });
        if (res) toast(res.status === 'accepted' ? t('hand_moved', { name: memberName(app.member(to)) }) : t('hand_sent', { name: memberName(app.member(to)) }), 'success');
    },

    approve(it) { return run('tick.approve', { entry_id: it.entry.id }); },
    reject(it) { return run('tick.reject', { entry_id: it.entry.id }); },
    decide(c, give) { return run('claim.decide', { id: c.id, give }); },
    claim(r, memberId) { return run('reward.claim', { reward_id: r.id, member_id: memberId }); },
    claimFor(r) {
        clear(menu);
        app.state.members.filter((m) => !m.left).forEach((m) => {
            const have = (m.points_total | 0) - (m.points_spent | 0);
            menu.appendChild(h('button.hc-menu-i', { type: 'button', disabled: have < r.cost, onclick: () => app.act.claim(r, m.id) }, [avatar(m, 24), h('span', null, memberName(m) + ' · ★ ' + num(have))]));
        });
    },
    reward(r) { rewardDialog(app, r); },
    newChore() { choreDialog(app, null); },
    sets() { setsDialog(app); },
    share() { shareDialog(); },
    startOver() { closeDialog(); app.store.wipe().then(() => { location.hash = ''; location.reload(); }); },

    async newHome(name) {
        try {
            const res = await account.create({ name, timezone: app.state.household.tz, week_start: app.state.household.week_start });
            closeDialog();
            app.me = await account.me().catch(() => app.me);
            funnel('household_created', { via: 'new' });
            openServer(res.id, res.state);
            try { history.replaceState(null, '', location.pathname + '?h=' + res.id); } catch (e) { /* старый движок */ }
        } catch (e) { fail(e, 'cc_new_home'); }
    },

    async pdf() {
        toast(t('pdf_making'), 'info');
        try {
            const { weekPdf } = await import('./pdf.js');
            const start = app.weekOf || E.weekStartOf(app.today, app.state.household.week_start || 1);
            const out = await weekPdf(app.state, start, app.today);
            const ok = window.__downloadFile ? await window.__downloadFile(out.blob, out.name) : false;
            if (ok !== false) funnel('pdf_saved', { pages: out.pages });
        } catch (e) { try { window.__handleError(e, 'cc_pdf', { say: t('pdf_failed') }); } catch (x) { /* ядро сайта ещё не поднялось */ } }
    },
});

/* ── «Поделиться»: нужен аккаунт; локальный график переедет после входа ──── */

function authUrls() {
    const back = '/' + CFG.locale + '/chore-chart';
    return { login: CFG.loginUrl + (CFG.loginUrl.indexOf('?') < 0 ? '?' : '&') + 'redirect=' + encodeURIComponent(back), register: CFG.registerUrl };
}

function shareDialog() {
    funnel('share_intent', { chores: app.state.chores.length });
    stage('share_intent');
    if (app.store.mode !== 'local') { peopleDialog(app); return; }
    if (CFG.boot.authed || window.__AUTH_USER) { moveToAccount(); return; }
    const u = authUrls();
    const go = (url) => async (ev) => {
        ev.preventDefault();
        lsSet(LS_MOVE, '1');
        try { await app.store.flush(); } catch (e) { /* запись уже на диске по таймеру */ }
        location.href = url;
    };
    dialog({
        title: t('share_title'),
        body: h('div.hc-form', null, [h('p.hc-p', null, t('share_body')), h('p.hc-hint', null, t('share_site', { host: location.host }))]),
        actions: [
            h('a.hc-btn.ghost', { href: u.login, onclick: go(u.login) }, t('sign_in')),
            h('a.hc-btn.primary', { href: u.register, onclick: go(u.register) }, t('sign_up')),
        ],
    });
}

/** Локальный график → дом на сервере, со всем, что в нём есть. */
async function moveToAccount(silent) {
    const local = app.store;
    if (!local || local.mode !== 'local' || !local.state) return false;
    if (!silent) toast(t('moving'), 'info');
    try {
        const payload = local.exportForImport();
        try { await local.flush(); } catch (x) { /* ключ на диск до запроса: повтор после обрыва отправит тот же */ }
        const res = await account.create(payload);
        lsSet(LS_MOVE, null);
        await local.wipe();
        funnel('household_created', { via: 'import', chores: res.state.chores.length, people: res.state.members.length });
        stage('household_created');
        openServer(res.id, res.state);
        if (!silent) { toast(t('moved'), 'success'); peopleDialog(app); }
        return true;
    } catch (e) {
        /* Отказ сервера (лимит людей) — решение, его не повторяют. Сбой связи — не решение: намерение остаётся,
           и следующая загрузка страницы повторит перенос тем же ключом (второго дома он не заведёт). */
        if (e instanceof Refusal) lsSet(LS_MOVE, null); else lsSet(LS_MOVE, '1');
        fail(e, 'cc_move');
        return false;
    }
}

/* ── шапка приложения, полоса людей, вкладки ───────────────────────────── */

const views = { today: renderToday, week: renderWeek, month: renderMonth, balance: renderBalance, stars: renderStars };

function render() {
    const s = app.store && app.store.state;
    if (!s) return;
    app.state = s;
    app.today = s.today || app.store.today();
    app.can = permissions(s);
    if (app.filter !== 'all' && app.filter !== 'mine' && !app.member(app.filter)) app.filter = 'all';
    app.wide = appEl.clientWidth >= 760;
    app.chartW = Math.max(260, Math.min(820, appEl.clientWidth - 48));
    const local = s.mode === 'local';
    const kids = s.members.some((m) => !m.left && m.role === 'child');
    const showStars = !local && (kids || (s.rewards || []).length > 0);
    if (app.view === 'stars' && !showStars) app.view = 'today';

    clear(appEl);

    if (local) {
        appEl.appendChild(h('div.hc-local', null, [
            h('span', null, t('local_note')),
            btn(t('share_cta'), () => shareDialog(), 'sm.primary'),
        ]));
    }
    if (app.store.offline || app.store.pending) {
        appEl.appendChild(h('div.hc-net', { role: 'status' }, app.store.pending ? plural('queued', app.store.pending) : t('offline')));
    }

    const homes = (app.me && app.me.households) || [];
    appEl.appendChild(h('div.hc-bar', null, [
        (!local && homes.length > 1)
            ? h('select.hc-in.hc-home-sel', { 'aria-label': t('switch_home'), onchange: (e) => switchHome(parseInt(e.target.value, 10)) },
                homes.map((x) => h('option', { value: x.id, selected: x.id === s.household.id }, x.name)))
            : h('h3.hc-home', null, s.household.name),
        h('div.hc-bar-a', null, [
            app.can.manage ? btn('+ ' + t('add_chore'), () => app.act.newChore(), 'primary') : null,
            app.can.manage ? btn(t('add_set'), () => app.act.sets(), 'ghost') : null,
            btn(t('people'), () => peopleDialog(app), 'ghost'),
            btn(t('settings'), () => homeDialog(app), 'ghost'),
        ]),
    ]));

    appEl.appendChild(h('div.hc-people', null, renderPeople(app)));

    const tabs = [['today', 'today'], ['week', 'week'], ['month', 'month'], ['balance', 'balance']].concat(showStars ? [['stars', 'stars']] : []);
    appEl.appendChild(seg(tabs.map((x) => ({ v: x[0], label: t(x[1]) })), app.view, (v) => {
        app.go({ view: v, dayOpen: null });
        stage('view_' + v);
    }, 'tabs'));

    const view = h('div.hc-view.' + app.view);
    let kidsNodes;
    try { kidsNodes = views[app.view](app); } catch (e) { kidsNodes = [h('p.hc-quiet', null, t('err'))]; if (window.__handleError) window.__handleError(e, 'cc_view_' + app.view, { say: t('err') }); }
    (function addAll(list) { list.forEach((n) => { if (Array.isArray(n)) addAll(n); else if (n) view.appendChild(n); }); })(kidsNodes);
    appEl.appendChild(view);

    /* Меню у кнопки: панель привязывается к дверям при первом их появлении (они рисуются скриптом). */
    try { if (menu && !menu.__apWired && window.__anchoredPopover && appEl.querySelector('[popovertarget]')) window.__anchoredPopover.scan(); } catch (e) { /* ярус без модуля */ }

    if (app.view === 'balance' && !local && app.oldest === null && !app._oldestAsked) {
        app._oldestAsked = true;
        app.store.history(E.addDays(app.today, -1), app.today).then((r) => { app.oldest = r.oldest || ''; render(); }).catch(() => {});
    }
}

/* ── вход в разные режимы ──────────────────────────────────────────────── */

function showApp() {
    if (startEl) startEl.hidden = true;
    root.removeAttribute('data-boot');
    appEl.hidden = false;
}

function attach(store) {
    if (app.store && app.store.stop) app.store.stop();
    app.store = store;
    app.hist = null; app.oldest = null; app._oldestAsked = false;
    store.on((s, why) => {
        if (why === 'gone') { onGone(); return; }
        render();
    });
    showApp();
    render();
}

function openLocal(store) {
    attach(store);
    stage('chart_local');
}

function openServer(hid, state) {
    const store = new ServerStore(hid, state);
    store.pollMs = CFG.pollMs || 8000;
    store.idleMs = CFG.pollIdleMs || 20000;
    attach(store);
    if (!state) store.refresh(true);
    store.start();
    stage('chart_shared');
}

async function switchHome(hid) {
    openServer(hid, null);
    try { history.replaceState(null, '', location.pathname + '?h=' + hid); } catch (e) { /* старый движок */ }
}

function onGone() {
    const why = app.store.goneWhy;
    if (app.store.stop) app.store.stop();
    closeDialog();
    toast(why === 'left' ? t('done') : t('err_gone'), why === 'left' ? 'success' : 'warning');
    account.me().then((me) => {
        app.me = me;
        if (me.households.length) switchHome(me.households[0].id);
        else { try { history.replaceState(null, '', location.pathname); } catch (e) { /* старый движок */ } location.reload(); }
    }).catch(() => location.reload());
}

/** Первый экран: выбран набор → локальный график (у вошедшего он тут же переезжает на сервер). */
async function startWith(kind) {
    const local = new LocalStore(CFG.guest);
    local.create(starter(kind, T, deviceTz(), weekStartGuess()));
    funnel('chart_started', { kind });
    openLocal(local);
    if (CFG.boot.authed) await moveToAccount(true);
    else if (kind === 'empty') peopleDialog(app);
}

/* ── приглашение ───────────────────────────────────────────────────────── */

async function joinFlow(token) {
    showApp();
    clear(appEl);
    let info;
    try { info = await account.preview(token); } catch (e) {
        if (!(e instanceof Refusal)) {
            /* Нет связи — это не «ссылка больше не работает»: приглашение не трогаем, страницу можно обновить. */
            if (!hashParam('join')) lsSet(LS_JOIN, token);
            fail(e, 'cc_join_preview');
            appEl.appendChild(h('div.hc-join', null, [h('p.hc-p', null, t('err_net')), btn(t('retry'), () => location.reload(), 'primary')]));
            return;
        }
        lsSet(LS_JOIN, null); clearHash();
        appEl.appendChild(h('div.hc-join', null, [h('p.hc-p', null, t('join_invalid')), btn(t('close'), () => location.reload(), 'ghost')]));
        return;
    }
    const authed = info.authed;
    const u = authUrls();
    const home = info.household.name;
    const accept = async () => {
        try {
            const res = await account.accept(token);
            lsSet(LS_JOIN, null); clearHash();
            funnel(res.joined ? 'invite_accepted' : 'invite_reopened', {});
            stage('invite_accepted');
            app.me = await account.me().catch(() => app.me);
            openServer(res.id, res.state);
            toast(res.joined ? t('join_done', { home }) : t('join_already'), 'success');
        } catch (e) {
            if (e instanceof Refusal) {
                lsSet(LS_JOIN, null);
                if (e.code === 'invite_invalid') { clear(appEl).appendChild(h('div.hc-join', null, [h('p.hc-p', null, t('join_invalid'))])); } else fail(e, 'cc_join');
                return;
            }
            /* Сбой связи или сервера — не отказ. Приглашение остаётся у страницы (после входа его хэша в адресе
               уже нет, и стёртое оно терялось бы целиком): принять можно снова — кнопкой или обновив страницу. */
            lsSet(LS_JOIN, token);
            fail(e, 'cc_join');
            clear(appEl).appendChild(h('div.hc-join', null, [
                h('h3.hc-join-t', null, t('join_title', { home })),
                h('p.hc-p', null, t('err_net')),
                btn(t('join_go'), accept, 'primary'),
            ]));
        }
    };
    if (authed && (info.already || lsGet(LS_JOIN) === token)) { accept(); return; }
    appEl.appendChild(h('div.hc-join', null, [
        h('h3.hc-join-t', null, t('join_title', { home })),
        h('p.hc-p', null, authed ? t('join_body') : t('join_login', { host: location.host })),
        authed ? btn(t('join_go'), accept, 'primary') : h('div.hc-inline', null, [
            h('a.hc-btn.ghost', { href: u.login, onclick: () => lsSet(LS_JOIN, token) }, t('sign_in')),
            h('a.hc-btn.primary', { href: u.register, onclick: () => lsSet(LS_JOIN, token) }, t('sign_up')),
        ]),
    ]));
    funnel('invite_opened', { authed });
}

/* ── приглашения из семьи (docs/why/family.md) ─────────────────────────── */

/* Родственник позвал в свой дом без ссылки: приглашение ждёт здесь, над графиком
   и над первым экраном. В дом человек входит, только нажав «Войти в дом»;
   «Отклонить» пригласившему не сообщается. */
function familyInvites(list) {
    const box = $('hcFamIn');
    if (!box) return;
    clear(box);
    const done = (card) => { card.remove(); box.hidden = !box.children.length; };
    (list || []).forEach((inv) => {
        const card = h('div.hc-join.hc-fam-card', { 'data-fam-invite': String(inv.id) });
        card.appendChild(h('h3.hc-join-t', null, t('join_title', { home: inv.home })));
        card.appendChild(h('p.hc-p', null, t('fam_in_body', { name: inv.from, role: t('role_' + inv.role) })));
        card.appendChild(h('div.hc-inline', null, [
            btn(t('fam_in_decline'), async () => {
                try { await account.familyDecline(inv.id); done(card); toast(t('fam_in_declined'), 'info'); } catch (e) { fail(e, 'cc_fam_decline'); }
            }, 'ghost'),
            btn(t('join_go'), async () => {
                try {
                    const res = await account.familyAccept(inv.id);
                    done(card);
                    funnel('invite_family_accepted', {});
                    app.me = await account.me().catch(() => app.me);
                    openServer(res.id, res.state);
                    toast(res.joined ? t('join_done', { home: inv.home }) : t('join_already'), 'success');
                } catch (e) {
                    fail(e, 'cc_fam_accept');
                    if (e instanceof Refusal) done(card);
                }
            }, 'primary'),
        ]));
        box.appendChild(card);
    });
    box.hidden = !box.children.length;
}

/* ── настенный экран ───────────────────────────────────────────────────── */

function displayFlow(token) {
    const store = new DisplayStore(token);
    store.pollMs = CFG.pollMs || 8000;
    const host = $('hcDisplay');
    host.hidden = false;
    mountDisplay(host, store, {});
    store.refresh(true);
    store.start();
    stage('display');
    funnel('display_opened', { w: window.innerWidth, h: window.innerHeight });
}

/* ── загрузка ──────────────────────────────────────────────────────────── */

async function boot() {
    const claim = (fn) => { try { if (window.__earlyAction) window.__earlyAction.claim(SLUG, fn || null); } catch (e) { /* шима нет — кнопки и так наши */ } };

    const display = hashParam('display');
    if (display) { claim(null); displayFlow(display); return; }

    const join = hashParam('join') || (CFG.boot.authed ? lsGet(LS_JOIN) : null);
    if (join) { claim(null); await joinFlow(join); return; }

    if (CFG.boot.authed && CFG.boot.me) familyInvites(CFG.boot.me.family_invites);

    if (CFG.boot.authed && CFG.boot.state) {
        claim(null);
        openServer(CFG.boot.state.household.id, CFG.boot.state);
        if (location.hash === '#balance') app.go({ view: 'balance' });
        return;
    }

    const local = new LocalStore(CFG.guest);
    const saved = LocalStore.hasChart() ? await local.load() : null;
    if (saved) {
        claim(null);
        openLocal(local);
        /* Вернулся со входа, куда ушёл по «Поделиться»: график переезжает сам. */
        if (CFG.boot.authed && (lsGet(LS_MOVE) === '1' || !app.me || !app.me.households.length)) await moveToAccount(lsGet(LS_MOVE) !== '1');
        return;
    }

    /* Первый экран — серверный: берём его кнопки себе; нажатие до скрипта доносит шим. */
    root.removeAttribute('data-boot');
    if (startEl) {
        startEl.hidden = false;
        startEl.addEventListener('click', (ev) => {
            const b = ev.target.closest('[data-early-action]');
            if (b) startWith(b.getAttribute('data-early-action'));
        });
    }
    stage('start_screen');
    claim((kind) => startWith(kind));
}

/* Ширина решает раскладку недели и размер графиков — перерисовываем, когда она переходит порог. */
try {
    let lastWide = null, lastW = 0;
    new ResizeObserver(() => {
        if (!app.store || !app.store.state || appEl.hidden) return;
        const wide = appEl.clientWidth >= 760;
        if (wide !== lastWide || Math.abs(appEl.clientWidth - lastW) > 60) { lastWide = wide; lastW = appEl.clientWidth; render(); }
    }).observe(appEl);
} catch (e) { /* без наблюдателя раскладка считается при каждой перерисовке */ }

window.addEventListener('hashchange', () => { if (hashParam('display') || hashParam('join')) location.reload(); });

boot().catch((e) => { if (window.__handleError) window.__handleError(e, 'cc_boot', { say: t('err') }); });
